/**
 * Subtask grouping for the plan. Tasks are one level deep: a subtask
 * points at a parent via parent_task_id.
 *
 * groupSubtasks splits a list into top-level tasks (input order
 * preserved) and a parent-id → children map — what the Plan table needs
 * to render a parent row with a caret/count and its nested children.
 * A subtask whose parent isn't in the same list (e.g. filtered to one
 * phase) is treated as top-level so it's never hidden.
 */
export interface TaskNode {
  id: number;
  parent_task_id: number | null;
}

export interface OrderedTask<T> {
  task: T;
  depth: 0 | 1;
}

export function groupSubtasks<T extends TaskNode>(
  tasks: T[],
): { topLevel: T[]; childrenByParent: Map<number, T[]> } {
  const present = new Set(tasks.map((t) => t.id));
  const childrenByParent = new Map<number, T[]>();
  const topLevel: T[] = [];
  for (const t of tasks) {
    const pid = t.parent_task_id;
    if (pid != null && present.has(pid)) {
      const arr = childrenByParent.get(pid) ?? [];
      arr.push(t);
      childrenByParent.set(pid, arr);
    } else {
      topLevel.push(t); // top-level, or an orphan subtask whose parent isn't here
    }
  }
  return { topLevel, childrenByParent };
}

/**
 * Flatten to render order — each top-level task followed by its
 * subtasks — with a depth for indentation. `collapsed` (a set of parent
 * ids) hides those parents' children. Order preserved within each group.
 */
export function orderWithSubtasks<T extends TaskNode>(
  tasks: T[],
  collapsed?: ReadonlySet<number>,
): OrderedTask<T>[] {
  const { topLevel, childrenByParent } = groupSubtasks(tasks);
  const out: OrderedTask<T>[] = [];
  for (const parent of topLevel) {
    out.push({ task: parent, depth: 0 });
    if (collapsed?.has(parent.id)) continue;
    for (const child of childrenByParent.get(parent.id) ?? []) {
      out.push({ task: child, depth: 1 });
    }
  }
  return out;
}
